import { auth, db } from "../config/firebase";
import {
    createUserWithEmailAndPassword,
    sendEmailVerification,
    validatePassword
} from "firebase/auth";
import { useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CssBaseline,
    Divider,
    Grid,
    InputAdornment,
    Snackbar,
    TextField,
    Typography,
    Paper,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import GoogleIcon from '@mui/icons-material/Google';
import { IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ReCAPTCHA from "react-google-recaptcha";
import { addDoc, setDoc, doc, collection } from "firebase/firestore";
import { USER_TYPES } from "../constants/userTypes";

const drawerBg = "background.paper";
const drawerBorder = "1px solid";
const drawerBorderColor = "divider";
const drawerBoxShadow = 1;
const drawerPadding = 4;
const drawerRadius = 3;

export const SignUp = () => {
    const navigate = useNavigate();
    const emailRef = useRef();
    const passwordRef = useRef();
    const usernameRef = useRef();
    const recaptchaRef = useRef(null);
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState("");
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);

    const [creating, setCreating] = useState(false);

    // Validate input fields
    const validateInputs = async () => {
        const emailValue = emailRef.current?.value || "";
        const passwordValue = passwordRef.current?.value || "";
        let valid = true;

        if (!emailValue || !/\S+@\S+\.\S+/.test(emailValue)) {
            setEmailError(true);
            setEmailErrorMessage("Please enter a valid email address.");
            valid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage("");
        }

        const status = await validatePassword(auth, passwordValue);
        if (!status.isValid) {
            setPasswordError(true);
            setPasswordErrorMessage("Password does not meet password requirements.");
            valid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage("");
        }

        return valid;
    };

    const handleClickShowPassword = () => setShowPassword(show => !show);

    const handleCloseError = () => setErrorOpen(false);

    const handleCloseSuccess = () => setSuccessOpen(false);

    const handleSignUp = async (event) => {
        event.preventDefault();
        if (creating) return;

        const emailValue = emailRef.current?.value || "";
        const passwordValue = passwordRef.current?.value || "";
        const usernameValue = usernameRef.current?.value || "";

        // Validate
        const isValid = await validateInputs();
        if (!isValid) return;

        setCreating(true);

        // Do ReCAPTCHA (invisible)
        if (siteKey && recaptchaRef.current) {
            try {
                //const token = await recaptchaRef.current.executeAsync();
                const token = await recaptchaRef.current.getValue();
                if (!token) {
                    setCreating(false);
                    return;
                }
                recaptchaRef.current.reset();
            } catch {
                setCreating(false);
                return;
            }
        }

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, emailValue, passwordValue);
            const user = userCredential.user;

            // Send email verification
            sendEmailVerification(user);

            // Create user doc in Firestore
            const userData = {
                name: usernameValue,
                bio: "This is my bio",
                picurl: "",
                email: emailValue,
                uid: user.uid,
                type: USER_TYPES.FREE,
            };
            // After sign up with createUserWithEmailAndPassword, user is automatically authenticated (firebase.auth().currentUser is set).
            // However, Firestore rules require that the document ID matches the UID and is written by the authenticated user.
            // Fix: Use setDoc with the UID as doc ID, not addDoc with random ID.
            await setDoc(doc(db, "user", user.uid), userData);

            setSuccessOpen(true);
            setTimeout(() => {
                setSuccessOpen(false);
                navigate("/myprojects");
            }, 1800);
        } catch (error) {
            if (error.code === "auth/email-already-in-use") {
                setEmailError(true);
                setEmailErrorMessage("Email already in use");
            } else {
                setEmailError(true);
                setEmailErrorMessage(error.message || "Signup failed");
            }
            setErrorOpen(true);
        }
        setCreating(false);
    };

    return (
        <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: "100vh", backgroundColor: "background.default" }}
        >
            <CssBaseline />
            <Paper
                elevation={drawerBoxShadow}
                sx={{
                    borderRadius: drawerRadius,
                    border: drawerBorder,
                    borderColor: drawerBorderColor,
                    background: drawerBg,
                    px: { xs: 2, sm: drawerPadding },
                    py: 5,
                    width: { xs: "98vw", sm: 410 },
                    maxWidth: 430,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <img
                    alt="NodeCanvas logo"
                    src="/logo192.png"
                    width={60}
                    height={60}
                    style={{ marginBottom: 16, opacity: 0.9 }}
                />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Create your NodeCanvas Account
                </Typography>
                <Typography
                    align="center"
                    color="text.secondary"
                    fontSize={15}
                    sx={{ mt: 1, mb: 2 }}
                >
                    Already have an account?{" "}
                    <Link to="/login">
                        <b>Sign in</b>
                    </Link>
                </Typography>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    onSubmit={handleSignUp}
                    sx={{ width: "100%" }}
                >
                    <TextField
                        id="email"
                        label="Email Address"
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        inputRef={emailRef}
                        error={emailError}
                        helperText={emailErrorMessage}
                        autoComplete="email"
                        type="email"
                        color={emailError ? "error" : "primary"}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailOutlinedIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <TextField
                        id="username"
                        label="Username"
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        inputRef={usernameRef}
                        autoComplete="username"
                        color="primary"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonOutlineOutlinedIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <TextField
                        id="password"
                        label="Password"
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        inputRef={passwordRef}
                        error={passwordError}
                        helperText={passwordErrorMessage}
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        color={passwordError ? "error" : "primary"}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOpenOutlinedIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        edge="end"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={e => e.preventDefault()}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />
                    {(passwordError || passwordErrorMessage !== "") && (
                        <Typography
                            component="ul"
                            variant="caption"
                            sx={{
                                listStyleType: "circle",
                                ml: 4,
                                color: passwordError ? 'error.main' : 'text.secondary',
                                mb: 2,
                                pr: 1,
                                fontSize: 13,
                            }}
                        >
                            <li>Password minimum 8 characters</li>
                            <li>At least one lowercase</li>
                            <li>At least one uppercase</li>
                            <li>At least one numeric digit</li>
                            <li>At least one special character</li>
                        </Typography>
                    )}
                     {siteKey && (
                        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                size="normal"
                                sitekey={siteKey}
                            />
                        </Box>
                    )}
                    <Button
                        type="submit"
                        size="large"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 1, mb: 2, fontWeight: 600, textTransform: "none" }}
                        disabled={creating}
                    >
                        {creating ? "Creating Account..." : "Sign Up"}
                    </Button>
                   
                </Box>
            </Paper>
            <Snackbar open={errorOpen} autoHideDuration={3800} onClose={handleCloseError}>
                <Alert
                    onClose={handleCloseError}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {emailErrorMessage}
                </Alert>
            </Snackbar>
            <Snackbar open={successOpen} autoHideDuration={1900} onClose={handleCloseSuccess}>
                <Alert
                    onClose={handleCloseSuccess}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Account created! Please verify your email.
                </Alert>
            </Snackbar>
        </Grid>
    );
};